/**
 * The non-interaction test, run over OpenAlex with no model in the loop.
 *
 * `runLinkScan` in `reason.ts` puts three questions to a model's search tool —
 * side A, side C, and the two together — and hands whatever came back to
 * `assess` in `core/link.ts`, which reads a linkage off counts. This module puts
 * the same three questions to a scholarly index instead, through the corpus
 * door the research rows already open (see the header of `core/sources.ts`),
 * and hands the answers to the same arithmetic. Nothing here calls a model,
 * nothing is priced, and nothing enters the corpus store: three keyless GETs,
 * one reading, and the works that earned it. `indexPiles.test.ts` reads this
 * file's imports to hold it to that.
 *
 * WHY A SECOND LOOK. The web gather is a model choosing which pages to open, and
 * a model that has read both literatures can steer around the paper that joins
 * them without meaning to. An index has no taste: the question is put as terms,
 * the answer is the top of a relevance ranking, and a work addressing both sides
 * is on that page or it is not. So `already-made` here is a stop of its own —
 * `core/rung.ts` reads it as `prior-art` — and `unjoined` here is a weaker null
 * than the web's, for the reason under `PER_QUERY`. `IndexLinkage` in
 * `core/types.ts` keeps the two readings apart on the record, and this module
 * only ever produces the second.
 *
 * THE SAME SHAPE AS `runLinkScan`, on purpose. The conjunction runs first and
 * alone: it is the only search that can produce a positive finding, and `assess`
 * tests `already-made` ahead of everything else, so a pair the index has already
 * joined costs one request rather than three — a public API that asks its
 * callers to be polite is not asked twice for what one answer settled. The two
 * side searches run together afterwards, since neither depends on the other.
 *
 * A FAILED REQUEST IS NO READING. Every reading below `already-made` is a null,
 * and a null read off a search that errored is not a null — `assess` says so and
 * lands `unsound`. A source check can carry on with three rows out of four
 * because its records are evidence added to a pool; here the three piles ARE the
 * instrument, and a scan with one of them missing could only store a record
 * saying the search did not complete, which stops nothing and asks nobody to
 * look again. So a failure comes back AS a failure, in the words `liveSources.ts`
 * uses for the same events, with every query that was issued before it, and the
 * caller stores nothing.
 */

import { assess, distinctiveTerms, type LinkDoc, type LinkPile, type Linkage } from '../core/link'
import { SOURCES, liveOriginUrl, wellFormed } from '../core/sources'
import type { IndexLinkage } from '../core/types'
import { canonicalUrl } from '../core/url'
import {
  LIVE_FAILURE_NOTE,
  LIVE_QUERY_CHARS,
  bareDoi,
  fromInvertedIndex,
  getJson,
  qs,
  type LiveFailureCode,
  type Transport,
} from './liveSources'

/**
 * Works read per query — one page, the index's own default, and never a second.
 *
 * This is the number that makes `unjoined` here a weaker null than Swanson's. He
 * could take non-interaction as evidence because MEDLINE was closed and he read
 * all of it; this reads the twenty-five works a relevance ranking put first, so
 * an absence on this page says only that the joining work, if there is one, did
 * not rank in the top twenty-five for the union of both vocabularies. That is
 * still a search that went looking for the opposite and failed — the bar
 * `core/link.ts` sets — and `docs/limits.md` says how far it reaches.
 * `already-made` needs no such qualification: a work on the page that addresses
 * both sides is on the page whatever lies below it.
 */
const PER_QUERY = 25

/**
 * The fields asked for, and only the ones something here reads: the two
 * identities an address can be built from, the title, and the abstract. A work
 * is a large object. `primary_location` — the venue — was on the first draft of
 * this list and came off it because nothing in a `LinkDoc` has anywhere to put a
 * venue, and a field fetched and dropped is bytes on every one of seventy-five
 * works for no reader's benefit.
 */
const SELECT = 'id,title,doi,abstract_inverted_index'

/** Long enough for a cold public index, short enough that the worst case — the
 *  conjunction, then both sides together — is two of these beside a model pass
 *  that takes longer. The figure `liveSources.ts` gives one row, for its reason. */
const INDEX_TIMEOUT_MS = 5000

/**
 * The registry row this module calls. Its `origin` is the ONE host these
 * requests may go to — the string `csp.test.ts` pairs with `wired` in
 * `connect-src` — and its `name` is what every failure note is written from, so
 * renaming the row renames it in every sentence at once. Read off the registry
 * rather than written here for the reason `LiveFailure` carries a name rather
 * than looking one up later: two spellings of one service are two services to a
 * reader.
 */
const ROW = SOURCES.find((s) => s.id === 'openalex')!

/** What a side is searched on: the shape `distinctiveTerms` reads, which is also
 *  what `endText` in `reason.ts` makes of either end of a bridge. */
export interface LinkSide {
  label: string
  claim: string
  evidence?: string[]
}

/**
 * How a scan can fail without a reading, closed, the way `LiveFailureCode` is.
 *
 * The four transport codes are `liveSources.ts`'s own, reused rather than renamed
 * so a test and a log read one vocabulary. `stopped` is the user's own press,
 * which no sentence may blame a service for. `nothing-to-search` is a side whose
 * every word was a stop word, which is no request at all — an empty `search=`
 * would have OpenAlex answer with its first page of everything, and a reading
 * built on that would be a reading of nothing dressed as a literature.
 */
export type IndexScanFailureCode = LiveFailureCode | 'stopped' | 'nothing-to-search'

/** A scan that produced no reading, and why. */
export interface IndexScanFailure {
  /** One sentence in `LIVE_FAILURE_NOTE`'s voice: it names the service and never
   *  the code, because a reader can act on "OpenAlex did not answer in time" and
   *  on precisely nothing else. */
  failed: string
  /** The machine half, for a caller deciding what to do and for a test — so that
   *  nothing anywhere has to read the sentence to find out what happened. */
  code: IndexScanFailureCode
  /** Every query issued before the failure, so an abandoned scan is exactly as
   *  auditable as a finished one. */
  queries: string[]
}

export interface IndexScanOptions {
  /** Injectable so the whole suite runs offline, as `LiveFetchOptions.fetchImpl`
   *  is: nothing in this repo has ever reached the network from a test. */
  fetch?: typeof globalThis.fetch
  /** The pass's signal, composed into every request the way `liveSources.ts`
   *  composes it: a Stop reaches the request in flight, and a request's own
   *  timer never reaches out. */
  signal?: AbortSignal
  /** The clock `scannedAt` is read from. */
  now?: () => number
  /** `nv.src.mailto`, for OpenAlex's polite pool. It authorises nothing, it
   *  identifies a caller, and it never reaches the stored record — see
   *  `worksUrl` for why the record and the request differ by exactly this. */
  mailto?: string
  timeoutMs?: number
}

export const INDEX_SCAN_STOPPED = 'The index scan was stopped before it finished.'
export const INDEX_SCAN_NOTHING_TO_SEARCH =
  'One side of the pair has no term an index could be searched for.'

/** The one predicate a caller branches on. */
export const indexScanFailed = (r: IndexLinkage | IndexScanFailure): r is IndexScanFailure =>
  'failed' in r

/**
 * The terms one side sends, held to the ceiling a source check keeps.
 *
 * `LIVE_QUERY_CHARS` is the promise the settings copy makes about what ONE
 * request sends a research row. A link scan sends the same row a different cut
 * of the same node — its distinctive terms, evidence pointers included, rather
 * than the first 256 characters of its label and claim — and the ceiling is kept
 * per side, measured over the words themselves, so the promise reads the same
 * for both passes. Whole terms only: a term cut in half is a word nobody wrote,
 * sent as though somebody had. The conjunction carries both sides' lists and so
 * may run to twice this, which is the two sides' promise added together and not
 * a third one.
 */
function capTerms(terms: string[]): string[] {
  const kept: string[] = []
  let length = 0
  for (const term of terms) {
    const next = length + term.length + (kept.length ? 1 : 0)
    if (next > LIVE_QUERY_CHARS) break
    kept.push(term)
    length = next
  }
  return kept
}

/**
 * The request for one pile, without the contact address. This string is what
 * `queries` records.
 *
 * THE QUERY SHAPE, and the one assumption in this module a fake `fetch` cannot
 * check. `search=` rather than `filter=title_and_abstract.search:`. The filter
 * matches only where this scan can read — a work returned on the strength of its
 * full text is one whose abstract may carry none of the terms, and `assess` then
 * scores it as a miss — so it looked like the closer fit. Two things decide
 * against it. OpenAlex documents its boolean operators on the `search`
 * parameter, and the join below leans on one of them; and a full-text hit the
 * abstract cannot confirm costs exactly one of the twenty-five slots and nothing
 * else, because the gate scores what it can read and never what the index claims
 * to have matched. Under the coverage floors that is a small price. A query the
 * index parses differently from how this comment describes it is not.
 *
 * The terms are joined with an explicit `OR`. The test wants a RANKING — the
 * works carrying the most of a side's vocabulary, and for the conjunction the
 * most of both — rather than the works carrying all of it, which for two dozen
 * terms is no work at all. It is written out rather than left to the parser's
 * default because this module cannot afford to depend on the default: were it
 * `AND`, an unwritten operator would turn every side search into an empty page
 * and every pair into `thin`, silently, on every scan. Where the default is `OR`
 * the word is redundant and costs nothing; where the operator is not parsed at
 * all it is a stop word the index discards. The one live request that verifies
 * this is a side search returning a page rather than nothing.
 *
 * The conjunction is the union of both sides' terms under the same `OR`, and it
 * is a conjunction only because of what happens next: a relevance ranking puts
 * the works matching the most terms first, a work drawing on both vocabularies
 * outscores one drawing on either, and `assess` then holds every returned work
 * to a coverage floor on EACH side. The search widens the net; the arithmetic is
 * the conjunction. A parenthesised `(a OR b) AND (c OR d)` would say it in the
 * query, and is not used because nothing here can verify the parser honours the
 * parentheses, and a query that quietly degraded to its bare terms would return
 * the page this one asks for on purpose.
 *
 * THE ADDRESS IS NOT PART OF THE QUESTION. `mailto` is a fact about who asked,
 * and `queries` is a record of what was asked: it exists so a reader can see what
 * an absence is an absence from, and it travels — into the stored exploration,
 * both Markdown exports and the manifest. An address in every link scan's record
 * would be a contact detail the user typed into one settings field, copied into
 * every file they ever export. So the request carries it and the record does
 * not; `scanIndexPiles` appends it one expression before the fetch.
 *
 * `wellFormed` is belt and braces: `tokenise` keeps letters and numbers only, so
 * a term cannot hold half a character, but the string is about to meet
 * `encodeURIComponent` outside any catch and `liveQuery` guards the same edge.
 */
function worksUrl(terms: string[]): string {
  return `${ROW.origin}/works?${qs({
    search: wellFormed(terms.join(' OR ')),
    'per-page': String(PER_QUERY),
    select: SELECT,
  })}`
}

/* Two one-line readers `liveSources.ts` keeps private. A one-liner copied is not
   the drift D13 is about — there is no rule in either to drift — and exporting
   them would be exporting a shape rather than a decision. */
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
const txt = (v: unknown): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '')

/**
 * One page of works as documents, or null for a container this build cannot read.
 *
 * Parsed the way the OpenAlex adapter in `liveSources.ts` parses the same shape —
 * every field optional, a junk item skipped, only a wrong container failing the
 * request — with two rules of its own about what a `LinkDoc` needs.
 *
 * THE ADDRESS IS THE DOI WHERE THERE IS ONE, and the OpenAlex id otherwise. Both
 * are built by `liveOriginUrl`, off the same `doi:` and `openalex:` ids the
 * adapter mints, so a work's address here is the address a cited passage of the
 * same work gets everywhere else — and the null that derivation returns for an
 * id that is not whole is honoured here too: a work whose identifiers this build
 * cannot turn into a door is skipped rather than given a door that would open on
 * the wrong record. The DOI comes first because `assess` reports the addresses
 * both sides returned, and doi.org is the one host two searches for one work
 * agree on.
 *
 * ONE WORK COUNTS ONCE within a page. The index does not return a work twice,
 * but the arithmetic downstream is a count of DOCUMENTS — `MIN_TERM_DOCS` is two
 * — and a page that somehow carried one work under two rows would make an
 * intermediate out of a single paper. That is the expensive error, and a set over
 * the canonical address closes it for the cost of one line.
 *
 * TEXT IS THE ABSTRACT, put back in order by `fromInvertedIndex`, and absent
 * where the index holds none — never an empty string. `LinkDoc.text` being
 * absent is what makes `assess` score the work against the titles floor, which
 * is the lower one on purpose (see `ADDRESSES_BOTH_TITLE`): a work read on its
 * title alone is thin evidence, and thin evidence gets the lower bar because the
 * error it causes is the expensive one. The reconstruction is lossy on
 * punctuation and carries no marker here, because nothing built from it is ever
 * shown: `joinedBy` keeps the url and the title, and the text goes with the pass.
 */
function readWorks(body: unknown): LinkDoc[] | null {
  const results = obj(body).results
  if (!Array.isArray(results)) return null
  const docs: LinkDoc[] = []
  const seen = new Set<string>()
  for (const item of results) {
    const w = obj(item)
    const title = txt(w.title)
    // A work nobody can name is not evidence a reader can go and check — the
    // adapter's own rule, for its reason.
    if (!title) continue
    const doi = bareDoi(w.doi)
    // `https://openalex.org/W2741809807` → `W2741809807`, as the adapter reads it.
    const wid = txt(w.id).split('/').pop() ?? ''
    const url =
      (doi && liveOriginUrl(`doi:${doi}`)) || (wid && liveOriginUrl(`openalex:${wid}`)) || null
    if (!url) continue
    const key = canonicalUrl(url)
    if (seen.has(key)) continue
    seen.add(key)
    const text = fromInvertedIndex(w.abstract_inverted_index)
    docs.push({ url, title, ...(text ? { text } : {}) })
  }
  return docs
}

/** The reading, the intermediates and the counts, straight off the `Linkage` and
 *  nothing else — the record is the arithmetic's, not this module's. */
function fromLinkage(l: Linkage, queries: string[], scannedAt: number): IndexLinkage {
  return {
    reading: l.reading,
    intermediates: l.intermediates.map(({ term, aDocs, cDocs }) => ({ term, aDocs, cDocs })),
    // `search`, never `fetch`. `JoiningSource.via` exists to keep a title match
    // and a page saying so apart, and an abstract the index holds is the first
    // kind: the index returned the record, and nothing here opened the page.
    joinedBy: l.joinedBy.map((d) => ({ url: d.url, title: d.title, via: 'search' as const })),
    queries,
    aDocs: l.aDocs,
    cDocs: l.cDocs,
    source: 'openalex',
    scannedAt,
  }
}

/**
 * The whole test, over OpenAlex: three searches, one reading, or the reason
 * there is none. Never throws.
 *
 * The conjunction first and alone, then both sides together, for the reasons in
 * the header — and the pass's signal is re-read after each round rather than in
 * the request, because a request cannot know whether the pass it was made for
 * still exists. `getJson` reports a Stop as a timeout, which is what it saw; the
 * round is where that becomes the user's own press rather than a service failing,
 * so that no sentence this module returns puts a third party's name on a Stop.
 */
export async function scanIndexPiles(
  a: LinkSide,
  c: LinkSide,
  opts: IndexScanOptions = {},
): Promise<IndexLinkage | IndexScanFailure> {
  const queries: string[] = []
  const fail = (code: IndexScanFailureCode, failed: string): IndexScanFailure => ({
    failed,
    code,
    queries,
  })
  const failNote = (code: LiveFailureCode) => fail(code, LIVE_FAILURE_NOTE[code](ROW.name))

  const fetchImpl = opts.fetch ?? globalThis.fetch
  if (typeof fetchImpl !== 'function') return failNote('network')

  // ONE term extractor, `distinctiveTerms`, because the terms searched for must
  // be the terms a returned work is scored against: a second extractor here
  // would let the search ask for one vocabulary and the gate demand another.
  const aTerms = capTerms(distinctiveTerms(a))
  const cTerms = capTerms(distinctiveTerms(c))
  if (!aTerms.length || !cTerms.length)
    return fail('nothing-to-search', INDEX_SCAN_NOTHING_TO_SEARCH)

  const transport: Transport = {
    fetchImpl,
    timeoutMs: opts.timeoutMs ?? INDEX_TIMEOUT_MS,
    // The address's funnel, as in `fetchLiveSources`: made well-formed where it
    // enters, because it is about to meet `encodeURIComponent`.
    mailto: wellFormed(opts.mailto ?? '').trim() || undefined,
    signal: opts.signal,
  }
  const now = opts.now ?? Date.now

  /** One question. Recorded before it is asked, so a request that never comes
   *  back is still on the record of what was looked for. */
  const search = async (terms: string[]): Promise<LinkPile | LiveFailureCode> => {
    const asked = worksUrl(terms)
    queries.push(asked)
    const sent = transport.mailto ? `${asked}&${qs({ mailto: transport.mailto })}` : asked
    const got = await getJson(sent, transport)
    if ('code' in got) return got.code
    const docs = readWorks(got.value)
    return docs ? { docs, sound: true } : 'malformed'
  }

  const conjunction = await search([...new Set([...aTerms, ...cTerms])])
  if (opts.signal?.aborted) return fail('stopped', INDEX_SCAN_STOPPED)
  if (typeof conjunction === 'string') return failNote(conjunction)

  // Sound because the join test scores a work against the two SIDES' terms and
  // a floor that belongs to the work, never against the side piles — `runLinkScan`
  // reads `already-made` off empty piles for the same reason.
  const empty: LinkPile = { docs: [], sound: true }
  const early = assess({ ...a, pile: empty }, { ...c, pile: empty }, conjunction)
  if (early.reading === 'already-made') return fromLinkage(early, queries, now())

  const [aPile, cPile] = await Promise.all([search(aTerms), search(cTerms)])
  if (opts.signal?.aborted) return fail('stopped', INDEX_SCAN_STOPPED)
  if (typeof aPile === 'string') return failNote(aPile)
  if (typeof cPile === 'string') return failNote(cPile)

  return fromLinkage(
    assess({ ...a, pile: aPile }, { ...c, pile: cPile }, conjunction),
    queries,
    now(),
  )
}
